import { ReactNode } from "react";
import { SwipeAction } from "react-swipeable-list";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { SwipeableActionButton } from "../../button/swipeableActionButton/SwipeableActionButton";
import { ActionButton } from "../components/ActionButton";
import { IconType } from "../../../icons/Icon";
import { showCustomConfirmDialog } from "../../dialogs/AppConfirmDialog";
import { ISwipableAction } from "../types/ItemsList.types";
import { useApiMutation } from "../../../infrastructure/hooks/useApiMutation";

type ActionButtonType = "mobile" | "desktop";

export class ActionGenerator<TItem extends { [key: string]: any }> {
  private mutate: ReturnType<typeof useApiMutation>["mutate"];

  constructor(mutate: ReturnType<typeof useApiMutation>["mutate"]) {
    this.mutate = mutate;
  }

  generateActions(
    item: TItem,
    actions: ISwipableAction<TItem>[],
    onEdit: (id: number) => void,
    buttonType: ActionButtonType,
    iconHeight?: string,
    iconWidth?: string
  ): ReactNode {
    const elements: JSX.Element[] = [];

    // Generate action buttons for each action
    actions.forEach((action, index) => {
      const element = this.createActionElement(
        action,
        item,
        index,
        buttonType,
        iconHeight,
        iconWidth
      );

      if (element) {
        elements.push(element);
      }
    });

    // Handle edit action
    const editAction = actions.find((action) => action.type === "edit");
    if (editAction) {
      const editElement = this.createEditAction(
        editAction,
        item,
        onEdit,
        buttonType,
        iconHeight,
        iconWidth
      );
      if (editElement) {
        elements.push(editElement);
      }
    }

    // Handle redirect actions
    const redirectActions = actions.filter(
      (action) => action.type === "redirect"
    );
    redirectActions.forEach((action, index) => {
      const redirectElement = this.createRedirectAction(
        action,
        item,
        index,
        buttonType,
        iconHeight,
        iconWidth
      );
      if (redirectElement) {
        elements.push(redirectElement);
      }
    });

    return elements.length > 0 ? <>{elements}</> : undefined;
  }

  private createActionElement(
    action: ISwipableAction<TItem>,
    item: TItem,
    index: number,
    buttonType: ActionButtonType,
    iconHeight?: string,
    iconWidth?: string
  ): JSX.Element | null {
    if (action.type === "edit" || action.type === "redirect") {
      return null; // These are handled separately
    }

    const isDisabled = action.disabled?.(item) ?? false;
    const onClick = this.createActionClickHandler(action, item);

    const key = `${item.id}_${action.type}_${index}_${buttonType}`;

    if (buttonType === "mobile") {
      return (
        <SwipeAction key={key} onClick={isDisabled ? () => {} : onClick}>
          <SwipeableActionButton icon={action.iconType} disabled={isDisabled} />
        </SwipeAction>
      );
    }

    return (
      <ActionButton
        key={key}
        iconType={action.iconType}
        disabled={isDisabled}
        onClick={isDisabled ? () => {} : onClick}
        iconHeight={iconHeight}
        iconWidth={iconWidth}
      />
    );
  }

  private createEditAction(
    action: ISwipableAction<TItem>,
    item: TItem,
    onEdit: (id: number) => void,
    buttonType: ActionButtonType,
    iconHeight?: string,
    iconWidth?: string
  ): JSX.Element | null {
    const key = `${item.id}_edit_${buttonType}`;
    const onClick = () => onEdit(item.id);

    if (buttonType === "mobile") {
      return (
        <RenderWhen key={key} condition={true}>
          <SwipeAction onClick={onClick}>
            <SwipeableActionButton icon={IconType.EditIcon} />
          </SwipeAction>
        </RenderWhen>
      );
    }

    return (
      <ActionButton
        key={key}
        iconType={IconType.EditIcon}
        onClick={onClick}
        iconHeight={iconHeight}
        iconWidth={iconWidth}
      />
    );
  }

  private createRedirectAction(
    action: ISwipableAction<TItem>,
    item: TItem,
    index: number,
    buttonType: ActionButtonType,
    iconHeight?: string,
    iconWidth?: string
  ): JSX.Element | null {
    if (!action.redirectOnClick || !action.iconType) {
      return null;
    }

    const key = `${item.id}_redirect_${index}_${buttonType}`;
    const onClick = () => action.redirectOnClick!(item);

    if (buttonType === "mobile") {
      return (
        <RenderWhen key={key} condition={true}>
          <SwipeAction onClick={onClick}>
            <SwipeableActionButton icon={action.iconType} />
          </SwipeAction>
        </RenderWhen>
      );
    }

    return (
      <ActionButton
        key={key}
        iconType={action.iconType}
        onClick={onClick}
        iconHeight={iconHeight}
        iconWidth={iconWidth}
      />
    );
  }

  private createActionClickHandler(
    action: ISwipableAction<TItem>,
    item: TItem
  ): () => void {
    if (action.onClick && action.type === "onClick") {
      return this.createOnClickHandler(action, item);
    }

    return this.createApiCallHandler(action, item);
  }

  private createOnClickHandler(
    action: ISwipableAction<TItem>,
    item: TItem
  ): () => void {
    const executeAction = () => action.onClick!(item);

    if (action.enablePrompt && action.promptMessage) {
      return () => {
        showCustomConfirmDialog(
          action.promptMessage!,
          executeAction,
          () => {},
          action.promptType || "warning",
          action.acceptBtnLabel,
          action.rejectBtnLabel
        );
      };
    }

    return executeAction;
  }

  private createApiCallHandler(
    action: ISwipableAction<TItem>,
    item: TItem
  ): () => void {
    const executeApiCall = () => {
      let endpoint = `${action.endpoint}?itemId=${item.id}`;

      if (action.additionalParams) {
        endpoint = `${endpoint}&${action.additionalParams(item)}`;
      }

      this.mutate(
        {
          endpoint,
          globalIsLoading: action.enableGlobalIsLoading ?? true,
          method: action.method || "put",
          invalidateKeys: action.invalidateKeys,
          disableSuccessToast: action.disableSuccessToast,
        },
        {
          onSuccess: action.onSuccess
            ? (data: unknown) => action.onSuccess!(data, item)
            : undefined,
        }
      );
    };

    if (action.enablePrompt && action.promptMessage) {
      return () => {
        showCustomConfirmDialog(
          action.promptMessage!,
          executeApiCall,
          () => {},
          action.promptType || "warning",
          action.acceptBtnLabel,
          action.rejectBtnLabel
        );
      };
    }

    return executeApiCall;
  }
}
